import { X, type LucideIcon } from "lucide-react";
import { useEffect, type ReactNode } from "react";

import { useCloseOnBack } from "../lib/useCloseOnBack";
import { useScrollLock } from "../lib/useScrollLock";

interface Props {
  title: string;
  /** 見出しの左に出す絵。下部ナビやメニューと同じものを使い、どの画面か一目で分かるように */
  icon?: LucideIcon;
  /** ヘッダー右側(X の左)の操作。「+」や「選択」など */
  actions?: ReactNode;
  onClose: () => void;
  /** PC のモーダル幅。リスト系(タグ管理など)=576px / グリッド系(お気に入りなど)=1024px */
  width?: "list" | "grid";
  /** シートの上に開く面(日記→アルバムから選ぶなど)は1段上に重ねる(→ SheetShell の elevated) */
  elevated?: boolean;
  children: ReactNode;
}

/**
 * お気に入り・ゴミ箱・タグ管理などの外殻。
 *
 * スマホ・タブレットでは全画面、PC(xl〜)では暗幕+中央モーダルとして開く。
 * 全画面だとサイドバーごと塗り替わって居場所を見失うため、マウス操作の PC では
 * 背景を残して重ねる。閉じる操作(X・戻る・背景クリック・Esc)もここが持つ。
 */
export default function OverlayShell({
  title,
  icon: Icon,
  actions,
  onClose,
  width = "list",
  elevated = false,
  children,
}: Props) {
  useCloseOnBack(onClose);
  useScrollLock();

  // 編集途中でも即閉じる(誤操作の予防より軽さを取る)。
  // 写真の詳細表示が上に開いているときは、詳細側が capture で先に受けて止める
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      className={`overlay-fade fixed inset-0 xl:flex xl:items-center xl:justify-center xl:bg-black/40 xl:p-7 ${
        elevated ? "z-57" : "z-50"
      }`}
    >
      {/* 背景クリックで閉じる(モーダルのときだけ存在する) */}
      <button
        type="button"
        aria-label="閉じる"
        onClick={onClose}
        className="hidden cursor-default xl:absolute xl:inset-0 xl:block"
      />
      <div
        className={`overlay-pop flex h-full flex-col overflow-y-auto overscroll-contain bg-[var(--color-bg)] xl:relative xl:h-auto xl:max-h-[85vh] xl:w-full xl:overflow-hidden xl:rounded-2xl xl:shadow-2xl ${
          width === "grid" ? "xl:max-w-[var(--overlay-grid-w)]" : "xl:max-w-[var(--overlay-list-w)]"
        }`}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-[var(--color-line)] bg-[var(--color-bg)] p-4 xl:static">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            {Icon !== undefined && (
              <Icon className="size-5 text-[var(--color-accent)]" aria-hidden />
            )}
            {title}
          </h2>
          <div className="flex items-center gap-4">
            {actions}
            <button
              type="button"
              onClick={onClose}
              aria-label="閉じる"
              className="-m-2 flex size-9 items-center justify-center rounded-full transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)]"
            >
              <X className="size-5 text-[var(--color-sub)]" aria-hidden />
            </button>
          </div>
        </header>
        <div className="xl:min-h-0 xl:flex-1 xl:overflow-y-auto xl:overscroll-contain">
          {children}
        </div>
      </div>
    </div>
  );
}
